import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SectionHeading from "../common/SectionHeading";
import { Stagger, StaggerItem } from "../motion/Stagger";
import NewsCard from "../news/NewsCard";
import { useNews } from "../../hooks/useNews";

export default function NewsPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { news, loading, error } = useNews(3);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        bgcolor: (th) =>
          th.palette.mode === "dark" ? "rgba(14,20,40,0.4)" : "rgba(255,255,255,0.55)",
        borderBlock: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          eyebrow={t("newsEyebrow")}
          title={t("newsTitle")}
          subtitle={t("newsLead")}
        />

        {error && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        ) : news.length === 0 && !error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <CircularProgress size={22} />
            <Typography color="text.secondary">{t("newsEmpty")}</Typography>
          </Stack>
        ) : (
          <Stagger>
            <Grid container spacing={3}>
              {news.map((item) => (
                <Grid item xs={12} md={4} key={item.id}>
                  <StaggerItem sx={{ height: "100%" }}>
                    <NewsCard item={item} />
                  </StaggerItem>
                </Grid>
              ))}
            </Grid>
          </Stagger>
        )}

        {news.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <Button
              size="large"
              variant="outlined"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate("/news")}
              sx={{
                borderColor: "divider",
                color: "text.primary",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
            >
              {t("newsViewAll")}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
